import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Calendar, Star, Trophy, Flame, Check, Lock, Gift } from 'lucide-react';
import { supabase } from '@/db/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface DailyTask {
  id: string;
  task_key: string;
  name: string;
  description: string;
  icon: string;
  reward_points: number;
  max_daily_count: number;
  sort_order: number;
}

interface TaskProgress {
  task_key: string;
  completed_count: number;
  last_completed_at: string;
}

const TaskCenter: React.FC = () => {
  const { user, profile, refreshProfile } = useAuth();
  const [tasks, setTasks] = useState<DailyTask[]>([]);
  const [progress, setProgress] = useState<Record<string, TaskProgress>>({});
  const [checkInInfo, setCheckInInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);

  const fetchTasks = async () => {
    const { data } = await supabase.from('daily_tasks').select('*').eq('is_enabled', true).order('sort_order');
    setTasks(data || []);
  };

  const fetchProgress = async () => {
    if (!user) return;
    const today = new Date().toISOString().split('T')[0];
    const { data } = await supabase
      .from('user_task_progress')
      .select('*')
      .eq('user_id', user.id)
      .eq('progress_date', today);
    
    const progressMap: Record<string, TaskProgress> = {};
    data?.forEach((p: any) => {
      progressMap[p.task_key] = p;
    });
    setProgress(progressMap);
  };

  const fetchCheckInInfo = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('check_in_records')
      .select('*')
      .eq('user_id', user.id)
      .order('check_in_date', { ascending: false })
      .limit(1)
      .single();
    setCheckInInfo(data);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchTasks(), fetchProgress(), fetchCheckInInfo()]);
      setLoading(false);
    };
    init();
  }, [user]);

  const handleCheckIn = async () => {
    if (!user) {
      toast.error('请先登录');
      return;
    }
    setCheckingIn(true);
    try {
      const { data, error } = await supabase.rpc('check_in');
      
      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; consecutive_days?: number; reward_points?: number };
      
      if (!result.success) {
        toast.error(result.error || '签到失败');
      } else {
        toast.success(`签到成功！连续签到 ${result.consecutive_days} 天，获得 ${result.reward_points} 积分 🎉`);
        await Promise.all([fetchProgress(), fetchCheckInInfo(), refreshProfile()]);
      }
    } catch (err: any) {
      toast.error(err.message || '签到失败');
    } finally {
      setCheckingIn(false);
    }
  };

  const getTaskProgress = (taskKey: string) => {
    return progress[taskKey] || { completed_count: 0 };
  };

  const isTaskCompleted = (task: DailyTask) => {
    const prog = getTaskProgress(task.task_key);
    return prog.completed_count >= task.max_daily_count;
  };

  const getProgressPercentage = (task: DailyTask) => {
    const prog = getTaskProgress(task.task_key);
    return (prog.completed_count / task.max_daily_count) * 100;
  };

  const todayCheckedIn = checkInInfo && checkInInfo.check_in_date === new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Trophy className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
            <p className="text-muted-foreground">加载中...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        {/* 头部 */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-4">
            <Trophy className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-extrabold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            每日任务中心
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            完成每日任务获得积分奖励，积分可用于兑换勋章和特权
          </p>
        </div>

        {/* 用户积分和签到信息 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-amber-500/5 to-amber-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Star className="h-4 w-4 text-amber-500" /> 我的积分
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">{profile?.points || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">完成任务获得更多积分</p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-orange-500/5 to-orange-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Flame className="h-4 w-4 text-orange-500" /> 连续签到
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{checkInInfo?.consecutive_days || 0} 天</div>
              <p className="text-xs text-muted-foreground mt-1">
                {checkInInfo?.consecutive_days >= 7 ? '已获得连续签到奖励 🎉' : '连续签到 7 天额外奖励'}
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/50 bg-gradient-to-br from-green-500/5 to-green-500/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Gift className="h-4 w-4 text-green-500" /> 今日进度
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {Object.values(progress).reduce((sum, p) => sum + p.completed_count, 0)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">已完成任务次数</p>
            </CardContent>
          </Card>
        </div>

        {/* 签到卡片 */}
        <Card className="rounded-2xl border-border/50 mb-8 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-32 translate-x-32" />
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold flex items-center gap-2">
                  <Calendar className="h-6 w-6 text-primary" />
                  每日签到
                </CardTitle>
                <CardDescription className="mt-2">
                  每天签到获得 10 积分，连续签到 7 天额外 +5 分，30 天额外 +20 分
                </CardDescription>
              </div>
              <Button
                size="lg"
                className="rounded-xl font-bold h-14 px-8 shadow-lg"
                disabled={todayCheckedIn || checkingIn}
                onClick={handleCheckIn}
              >
                {todayCheckedIn ? (
                  <>
                    <Check className="h-5 w-5 mr-2" />
                    今日已签到
                  </>
                ) : checkingIn ? (
                  '签到中...'
                ) : (
                  <>
                    <Calendar className="h-5 w-5 mr-2" />
                    立即签到
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* 任务列表 */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-6">每日任务</h2>
          <div className="grid grid-cols-1 gap-4">
            {tasks.map((task) => {
              const completed = isTaskCompleted(task);
              const prog = getTaskProgress(task.task_key);
              const progressPercent = getProgressPercentage(task);
              return (
                <Card
                  key={task.id}
                  className={cn(
                    "rounded-2xl border-border/50 transition-all duration-300",
                    completed ? "bg-muted/30" : "hover:shadow-lg"
                  )}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      {/* 图标 */}
                      <div className={cn(
                        "text-5xl flex-shrink-0",
                        completed && "opacity-50 grayscale"
                      )}>
                        {task.icon}
                      </div>

                      {/* 任务信息 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold">{task.name}</h3>
                          {completed && (
                            <Badge className="bg-green-500 text-white border-none">
                              <Check className="h-3 w-3 mr-1" /> 已完成
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{task.description}</p>
                        
                        {/* 进度条 */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>进度: {prog.completed_count} / {task.max_daily_count}</span>
                            <span className="font-bold text-primary">+{task.reward_points} 积分/次</span>
                          </div>
                          <Progress value={progressPercent} className="h-2" />
                        </div>
                      </div>

                      {/* 状态图标 */}
                      <div className="flex-shrink-0">
                        {completed ? (
                          <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                            <Check className="h-6 w-6 text-green-500" />
                          </div>
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <Star className="h-6 w-6 text-primary" />
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* 提示信息 */}
        <Card className="rounded-2xl border-border/50 mt-8 bg-muted/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Trophy className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-bold mb-2">任务说明</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• 每日任务在每天 00:00 重置</li>
                  <li>• 部分任务可以多次完成，每次都能获得积分奖励</li>
                  <li>• 连续签到可获得额外奖励：7 天 +5 分，30 天 +20 分</li>
                  <li>• 积分可用于勋章商城兑换专属勋章和特权</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default TaskCenter;
